import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { initAudio, startLobbyMusic, stopAllMusic } from '../lib/SoundManager';
import MusicToggle from './MusicToggle';
import BattleField from './battle/BattleField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated sword icon for waiting players
const SwordIcon = ({ filled }) => (
  <motion.div
    animate={filled ? { scale: [1, 1.15, 1], opacity: [0.8, 1, 0.8] } : {}}
    transition={{ duration: 1.5, repeat: Infinity }}
    style={{
      width: 48, height: 48, borderRadius: 12,
      background: filled
        ? 'linear-gradient(145deg, #fbbf24, #d97706)'
        : 'rgba(255,255,255,0.05)',
      border: `2px solid ${filled ? '#fbbf24' : 'rgba(255,255,255,0.1)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: filled ? '0 0 16px rgba(251,191,36,0.5)' : 'none',
    }}
  >
    <span style={{ fontSize: 22, filter: filled ? 'none' : 'grayscale(1) opacity(0.3)' }}>
      {filled ? '♛' : '○'}
    </span>
  </motion.div>
);

// Ornate divider
const Divider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
    <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(251,191,36,0.3))' }} />
    <span style={{ color: 'rgba(251,191,36,0.5)', fontSize: 18 }}>✦</span>
    <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, rgba(251,191,36,0.3))' }} />
  </div>
);

const Lobby = ({ user, onGameStart }) => {
  const [queueSize, setQueueSize] = useState(0);
  const [matchedGameId, setMatchedGameId] = useState(null);
  const [dots, setDots] = useState('');
  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null);
  const dotsRef = useRef(null);
  const queueChannelRef = useRef(null);
  const gameChannelRef = useRef(null);
  const hasNavigatedRef = useRef(false);

  // Animated dots
  useEffect(() => {
    dotsRef.current = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 500);
    return () => clearInterval(dotsRef.current);
  }, []);

  useEffect(() => {
    if (matchedGameId && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      toast.success('Match trovato! Inizio partita...');
      onGameStart(matchedGameId);
      navigate(`/game/${matchedGameId}`);
    }
  }, [matchedGameId, navigate, onGameStart]);

  const checkMatchmakingStatus = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/game/join-matchmaking`, { user_id: user.user_id });
      if (response.data.matched) {
        setMatchedGameId(response.data.game_id);
      } else {
        setQueueSize(response.data.queue_size);
      }
    } catch (error) {
      toast.error('Errore matchmaking');
    }
  }, [user.user_id]);

  const subscribeToQueue = useCallback(() => {
    const queueChannel = supabase.channel('matchmaking_queue_updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matchmaking_queue' }, (payload) => {
        if (payload.eventType === 'DELETE' && payload.old.user_id === user.user_id) checkMatchmakingStatus();
        checkMatchmakingStatus();
      }).subscribe();
    queueChannelRef.current = queueChannel;
    return () => { queueChannel.unsubscribe(); supabase.removeChannel(queueChannel); };
  }, [user.user_id, checkMatchmakingStatus]);

  const subscribeToGameCreation = useCallback(() => {
    const gameChannel = supabase.channel('game_sessions_creation')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'game_sessions' }, (payload) => {
        const players = payload.new.players || [];
        if (players.some(p => p.user_id === user.user_id)) setMatchedGameId(payload.new.id);
      }).subscribe();
    gameChannelRef.current = gameChannel;
    return () => { gameChannel.unsubscribe(); supabase.removeChannel(gameChannel); };
  }, [user.user_id]);

  useEffect(() => {
    initAudio(); startLobbyMusic();
    checkMatchmakingStatus();
    pollingIntervalRef.current = setInterval(checkMatchmakingStatus, 2000);
    const unsubQ = subscribeToQueue();
    const unsubG = subscribeToGameCreation();
    return () => {
      stopAllMusic();
      clearInterval(pollingIntervalRef.current);
      unsubQ(); unsubG();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100dvh',
      background: '#03050a',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '20px 16px', position: 'relative', overflow: 'hidden',
    }}>
      {/* The host gathers on the field while we wait */}
      <BattleField kingdom="Unknown" dimmed />

      <MusicToggle />

      {/* Back button */}
      <button onClick={() => navigate('/kingdom')} style={{
        position: 'fixed', top: 14, left: 16, zIndex: 50,
        background: 'rgba(15,15,20,0.85)', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10,
        padding: '6px 14px', color: '#fbbf24', fontSize: 13, fontWeight: 700,
        cursor: 'pointer',
      }}>
        ← Regni
      </button>

      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480 }}
      >
        {/* Top ornament */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ fontSize: 28, color: '#fbbf24', letterSpacing: 12 }}
          >
            ✦ ♛ ✦
          </motion.div>
        </div>

        {/* Main card */}
        <div style={{
          background: 'linear-gradient(160deg, rgba(15,12,8,0.97) 0%, rgba(10,8,5,0.98) 100%)',
          borderRadius: 24,
          border: '1px solid rgba(251,191,36,0.25)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.8), 0 24px 60px rgba(0,0,0,0.8), 0 0 60px rgba(251,191,36,0.05)',
          padding: '36px 32px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Top corner ornaments */}
          {['top-left', 'top-right'].map(pos => (
            <div key={pos} style={{
              position: 'absolute',
              top: 12, [pos === 'top-left' ? 'left' : 'right']: 12,
              width: 32, height: 32,
              borderTop: '2px solid rgba(251,191,36,0.3)',
              [pos === 'top-left' ? 'borderLeft' : 'borderRight']: '2px solid rgba(251,191,36,0.3)',
              borderRadius: pos === 'top-left' ? '6px 0 0 0' : '0 6px 0 0',
            }} />
          ))}

          {/* Title section */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.2em',
              color: 'rgba(251,191,36,0.5)', textTransform: 'uppercase', marginBottom: 10,
            }}>
              The Last Kingdom
            </div>
            <h1 style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 28, fontWeight: 900, margin: '0 0 6px',
              background: 'linear-gradient(to bottom, #fff 0%, #FFEC80 40%, #FFD700 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Sala d'attesa
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
              Le armate si radunano{dots}
            </p>
          </div>

          <Divider />

          {/* Player slots */}
          <div style={{ marginBottom: 28 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.15em',
              color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase',
              textAlign: 'center', marginBottom: 16,
            }}>
              Sovrani in attesa
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              {[...Array(4)].map((_, i) => (
                <SwordIcon key={i} filled={i < queueSize} />
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <span style={{
                fontFamily: "'Cinzel', serif",
                fontSize: 36, fontWeight: 900,
                background: 'linear-gradient(to bottom, #fff, #fbbf24)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {queueSize}
              </span>
              <span style={{ fontSize: 20, color: 'rgba(251,191,36,0.4)', fontWeight: 700 }}> / 4</span>
            </div>
          </div>

          <Divider />

          {/* Status */}
          <div style={{ textAlign: 'center' }}>
            <motion.div
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.2)',
                borderRadius: 10, padding: '8px 20px',
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.3, 1], backgroundColor: ['#fbbf24', '#f59e0b', '#fbbf24'] }}
                transition={{ duration: 1, repeat: Infinity }}
                style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24' }}
              />
              <span style={{ fontSize: 13, color: '#fbbf24', fontWeight: 700, letterSpacing: '0.05em' }}>
                {queueSize >= 4 ? 'Avvio partita...' : 'In ricerca avversari'}
              </span>
            </motion.div>

            {/* Progress bar */}
            <div style={{
              marginTop: 16, height: 4, borderRadius: 2,
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}>
              <motion.div
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  height: '100%', width: '40%',
                  background: 'linear-gradient(to right, transparent, #fbbf24, transparent)',
                  borderRadius: 2,
                }}
              />
            </div>
          </div>

          {/* Bottom corners */}
          {['bottom-left', 'bottom-right'].map(pos => (
            <div key={pos} style={{
              position: 'absolute',
              bottom: 12, [pos === 'bottom-left' ? 'left' : 'right']: 12,
              width: 32, height: 32,
              borderBottom: '2px solid rgba(251,191,36,0.3)',
              [pos === 'bottom-left' ? 'borderLeft' : 'borderRight']: '2px solid rgba(251,191,36,0.3)',
              borderRadius: pos === 'bottom-left' ? '0 0 0 6px' : '0 0 6px 0',
            }} />
          ))}
        </div>

        {/* User info */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          style={{
            textAlign: 'center', marginTop: 16,
            fontSize: 12, color: 'rgba(255,255,255,0.3)',
          }}
        >
          Connesso come <span style={{ color: '#fbbf24', fontWeight: 700 }}>{user.username}</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Lobby;
